function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { spacing, borderRadius, themeLight, icons } from '@naf/theme';
import { Loader } from '@naf/loader';
import { Text, TextVariant } from '@naf/text';
import isPropValid from '@emotion/is-prop-valid';
const propValidationConfig = {
  shouldForwardProp: prop => isPropValid(prop.toString())
};
const defaultState = {
  isLoading: false,
  disabled: false,
  size: 'medium',
  variant: 'signature',
  width: 'content',
  isNotClickable: false
};
const resetButtonStyles = css(["border:none;margin:0;padding:0;width:auto;overflow:visible;background:transparent;color:inherit;font:inherit;line-height:normal;touch-action:manipulation;appearance:none !important;"]);
const notDisabled = ':not([disabled]):not([data-disabled])';
const commonStyles = css(["", ";display:inline-flex;justify-content:center;align-items:center;text-decoration:none;border-radius:", ";transition:background 0.2s ease,border-color 0.2s ease;cursor:pointer;width:fit-content;box-sizing:border-box;:focus,:focus-visible{outline:solid 1px;}&[disabled],&[data-disabled],&:disabled{border:none;cursor:not-allowed;color:", ";background:", ";&:visited{color:", ";background:", ";}}"], resetButtonStyles, borderRadius.L, ({
  theme
}) => theme.typography ? theme.typography.disabledText : themeLight.typography.disabledText, ({
  theme
}) => theme.componentColors ? theme.componentColors.cta.disabled : themeLight.componentColors.cta.disabled, ({
  theme
}) => theme.typography ? theme.typography.disabledText : themeLight.typography.disabledText, ({
  theme
}) => theme.componentColors ? theme.componentColors.cta.disabled : themeLight.componentColors.cta.disabled);
function specificVariantStyles(variant) {
  switch (variant) {
    case 'signature':
      return css(["&", "{background:", ";color:", ";}&:visited", "{color:", ";}&:hover", ",&:focus", "{background:", ";color:", ";}"], notDisabled, ({
        theme
      }) => theme.componentColors ? theme.componentColors.cta.primary : themeLight.componentColors.cta.primary, ({
        theme
      }) => theme.typography ? theme.typography.brandText : themeLight.typography.brandText, notDisabled, ({
        theme
      }) => theme.typography ? theme.typography.brandText : themeLight.typography.brandText, notDisabled, notDisabled, ({
        theme
      }) => theme.componentColors ? theme.componentColors.cta.primaryHover : themeLight.componentColors.cta.primaryHover, ({
        theme
      }) => theme.typography ? theme.typography.brandText : themeLight.typography.brandText);
    case 'secondary':
      return css(["&", "{background:", ";color:", ";}&:visited", "{color:", ";}&:hover", ",&:focus", "{background:", ";color:", ";}"], notDisabled, ({
        theme
      }) => theme.componentColors ? theme.componentColors.cta.secondary : themeLight.componentColors.cta.secondary, ({
        theme
      }) => theme.typography ? theme.typography.brandText : themeLight.typography.brandText, notDisabled, ({
        theme
      }) => theme.typography ? theme.typography.brandText : themeLight.typography.brandText, notDisabled, notDisabled, ({
        theme
      }) => theme.componentColors ? theme.componentColors.cta.secondaryHover : themeLight.componentColors.cta.secondaryHover, ({
        theme
      }) => theme.typography ? theme.typography.brandText : themeLight.typography.brandText);
    case 'outline':
      return css(["padding:0 calc(", " - 1px);border:1px solid;border-color:", ";&", "{background:'transparent';color:", ";}&:visited", "{color:", ";}&:hover", ",&:focus", "{border-color:", ";color:", ";}"], spacing.space24, ({
        theme
      }) => theme.border ? theme.border.heavy : themeLight.border.heavy, notDisabled, ({
        theme
      }) => theme.typography ? theme.typography.defaultText : themeLight.typography.defaultText, notDisabled, ({
        theme
      }) => theme.typography ? theme.typography.defaultText : themeLight.typography.defaultText, notDisabled, notDisabled, ({
        theme
      }) => theme.typography ? theme.typography.defaultText : themeLight.typography.defaultText, ({
        theme
      }) => theme.typography ? theme.typography.defaultText : themeLight.typography.defaultText);
    case 'spruce':
      return css(["&", "{background:", ";color:", ";}&:visited", "{color:", ";}&:hover", ",&:focus", "{background:", ";color:", ";}"], notDisabled, ({
        theme
      }) => theme.componentColors ? theme.componentColors.cta.secondaryInverted : themeLight.componentColors.cta.secondaryInverted, ({
        theme
      }) => theme.typography ? theme.typography.brandTextInverted : themeLight.typography.brandTextInverted, notDisabled, ({
        theme
      }) => theme.typography ? theme.typography.brandTextInverted : themeLight.typography.brandTextInverted, notDisabled, notDisabled, ({
        theme
      }) => theme.componentColors ? theme.componentColors.cta.secondaryInvertedHover : themeLight.componentColors.cta.secondaryInvertedHover, ({
        theme
      }) => theme.typography ? theme.typography.brandText : themeLight.typography.brandText);
    case 'spruce-secondary':
      return css(["&", "{background:", ";color:", ";}&:visited", "{color:", ";}&:hover", ",&:focus", "{background:", ";color:", ";}"], notDisabled, ({
        theme
      }) => theme.componentColors ? theme.componentColors.cta.secondaryInverted : themeLight.componentColors.cta.secondaryInverted, ({
        theme
      }) => theme.typography ? theme.typography.brandTextInverted : themeLight.typography.brandTextInverted, notDisabled, ({
        theme
      }) => theme.typography ? theme.typography.brandTextInverted : themeLight.typography.brandTextInverted, notDisabled, notDisabled, ({
        theme
      }) => theme.componentColors ? theme.componentColors.cta.secondaryHover : themeLight.componentColors.cta.secondaryHover, ({
        theme
      }) => theme.typography ? theme.typography.brandText : themeLight.typography.brandText);
    case 'outline-inverted':
      return css(["padding:0 calc(", " - 1px);border:1px solid;border-color:", ";&", "{background:'transparent';color:", ";}&:visited", "{color:", ";}&:hover", ",&:focus", "{border-color:", ";color:", ";}"], spacing.space24, ({
        theme
      }) => theme.border ? theme.border.heavyInverted : themeLight.border.heavyInverted, notDisabled, ({
        theme
      }) => theme.typography ? theme.typography.defaultTextInverted : themeLight.typography.defaultTextInverted, notDisabled, ({
        theme
      }) => theme.typography ? theme.typography.defaultTextInverted : themeLight.typography.defaultTextInverted, notDisabled, notDisabled, ({
        theme
      }) => theme.border ? theme.border.defaultInverted : themeLight.border.defaultInverted, ({
        theme
      }) => theme.typography ? theme.typography.defaultTextInverted : themeLight.typography.defaultTextInverted);
    // this is a temporary solution, waiting for new NAF colors epic
    case 'danger':
      return css(["&", "{background:#b02422;color:", ";}&:visited", "{background:#871d1e;color:", ";}&:hover", ",&:focus", "{background:#c92a27;color:", ";}"], notDisabled, ({
        theme
      }) => theme.typography ? theme.typography.defaultTextInverted : themeLight.typography.defaultTextInverted, notDisabled, ({
        theme
      }) => theme.typography ? theme.typography.defaultTextInverted : themeLight.typography.defaultTextInverted, notDisabled, notDisabled, ({
        theme
      }) => theme.typography ? theme.typography.defaultTextInverted : themeLight.typography.defaultTextInverted);
    default:
      return null;
  }
}
function variantStyles(variant, isLoading) {
  if (isLoading) {
    const commonLoadingStyles = css(["background:", ";color:", ";"], ({
      theme
    }) => theme.componentColors ? theme.componentColors.cta.disabled : themeLight.componentColors.cta.disabled, ({
      theme
    }) => theme.typography ? theme.typography.disabledText : themeLight.typography.disabledText);
    const loadingStyles = css(["cursor:default;border:none;", " &:hover,&:focus,&:visited{", "}"], commonLoadingStyles, commonLoadingStyles);
    return css(["", " &[disabled],&[data-disabled],&:disabled{", "}"], loadingStyles, loadingStyles);
  }
  const commonVariantStyle = !!variant ? css(["&:hover{text-decoration:none;}"]) : null;
  return [commonVariantStyle, specificVariantStyles(variant)];
}
function sizeStyles(size) {
  switch (size) {
    case 'small':
      return css(["height:44px;padding:0 ", ";"], spacing.space24);
    case 'medium':
      return css(["height:", ";padding:0 ", ";"], spacing.space48, spacing.space24);
    case 'large':
      return css(["height:", ";padding:0 ", ";"], spacing.space56, spacing.space24);
    default:
      return null;
  }
}
function widthStyles(width) {
  switch (width) {
    case 'half':
      return css(["width:50%;"]);
    case 'full':
      return css(["width:100%;"]);
    default:
      return null;
  }
}
function toggleStyles(props) {
  const variant = props.variant || defaultState.variant;
  const size = props.size || defaultState.size;
  const width = props.width || defaultState.width;
  const isLoading = props.isLoading || defaultState.isLoading;
  return [variantStyles(variant, isLoading), sizeStyles(size), widthStyles(width)];
}
const StyledButton = styled.button.withConfig(propValidationConfig).withConfig({
  displayName: "button__StyledButton",
  componentId: "sc-1yhofs5-0"
})(["", " ", ""], commonStyles, props => toggleStyles(props));
const StyledAnchor = styled.a.withConfig(propValidationConfig).withConfig({
  displayName: "button__StyledAnchor",
  componentId: "sc-1yhofs5-1"
})(["", " ", ""], commonStyles, props => toggleStyles(props));
const StyledPlaceholderDiv = styled.div.withConfig(propValidationConfig).withConfig({
  displayName: "button__StyledPlaceholderDiv",
  componentId: "sc-1yhofs5-2"
})(["", ";cursor:default;", ""], commonStyles, props => toggleStyles(props));
const LoadingButton = styled.div.withConfig({
  displayName: "button__LoadingButton",
  componentId: "sc-1yhofs5-3"
})(["display:flex;align-items:center;> div{margin-right:", ";}"], spacing.space8);
const StyledText = styled(Text).withConfig({
  displayName: "button__StyledText",
  componentId: "sc-1yhofs5-4"
})(["display:flex;align-items:center;svg{font-size:", ";}"], icons.s);
const ButtonContent = ({
  isLoading,
  loadingLabel,
  size,
  children
}) => {
  const computedVariant = size === 'small' ? TextVariant.ButtonSmall : TextVariant.Button;
  return isLoading ? /*#__PURE__*/React.createElement(LoadingButton, null, /*#__PURE__*/React.createElement(Loader, {
    variant: "neutral",
    size: "small"
  }), /*#__PURE__*/React.createElement(Text, {
    variant: computedVariant
  }, loadingLabel || 'Laster...')) : /*#__PURE__*/React.createElement(StyledText, {
    variant: computedVariant
  }, children);
};
export const Button = /*#__PURE__*/forwardRef(function ({
  isLoading = defaultState.isLoading,
  loadingLabel = defaultState.loadingLabel,
  disabled = defaultState.disabled,
  size = defaultState.size,
  variant = defaultState.variant,
  width = defaultState.width,
  isNotClickable = defaultState.isNotClickable,
  children,
  ...rest
}, ref) {
  const isDisabled = disabled || isLoading;
  if (isNotClickable) {
    return /*#__PURE__*/React.createElement(StyledPlaceholderDiv, {
      isLoading: isLoading,
      size: size,
      variant: variant,
      width: width,
      disabled: disabled
    }, /*#__PURE__*/React.createElement(ButtonContent, {
      isLoading: isLoading,
      loadingLabel: loadingLabel,
      size: size
    }, children));
  }
  if (rest.href) {
    const {
      href,
      onClick,
      ...other
    } = rest;
    return /*#__PURE__*/React.createElement(StyledAnchor, _extends({
      ref: ref,
      href: !isDisabled ? href : undefined,
      onClick: e => {
        if (onClick && !isDisabled) onClick(e);
      },
      "data-disabled": isDisabled ? true : undefined,
      isLoading: isLoading,
      size: size,
      variant: variant,
      width: width
    }, other), /*#__PURE__*/React.createElement(ButtonContent, {
      isLoading: isLoading,
      loadingLabel: loadingLabel,
      size: size
    }, children));
  }
  if (rest.onClick) {
    const {
      type,
      onClick,
      ...other
    } = rest;
    return /*#__PURE__*/React.createElement(StyledButton, _extends({
      ref: ref,
      type: type,
      onClick: e => {
        if (onClick) onClick(e);
      },
      isLoading: isLoading,
      disabled: isDisabled,
      size: size,
      variant: variant,
      width: width
    }, other), /*#__PURE__*/React.createElement(ButtonContent, {
      isLoading: isLoading,
      loadingLabel: loadingLabel,
      size: size
    }, children));
  }
  const {
    type,
    onClick,
    ...other
  } = rest;
  return /*#__PURE__*/React.createElement(StyledButton, _extends({
    ref: ref,
    type: type,
    onClick: e => {
      if (onClick) onClick(e);
    },
    isLoading: isLoading,
    disabled: isDisabled,
    size: size,
    variant: variant,
    width: width
  }, other), /*#__PURE__*/React.createElement(ButtonContent, {
    isLoading: isLoading,
    loadingLabel: loadingLabel,
    size: size
  }, children));
});
export default Button;
//# sourceMappingURL=button.js.map